// MCHD Room Scheduler - Global Theme Configuration
// Modern theme configuration for Mantine UI components

import { createTheme, MantineColorsTuple } from '@mantine/core';

// Custom color palette
const mchdBlue: MantineColorsTuple = [
  '#e6f7ff',
  '#b3e5fc',
  '#80d4fa',
  '#4dc3f7',
  '#28A8E0', // Primary color
  '#1e8bc3',
  '#1a7bb3',
  '#156b9e',
  '#105b89',
  '#0b4b74'
];

const mchdGray: MantineColorsTuple = [
  '#f8f9fa',
  '#F4F7FE', // Secondary background
  '#e9ecef',
  '#dee2e6',
  '#ced4da',
  '#adb5bd',
  '#6c757d',
  '#495057',
  '#343a40',
  '#212529'
];

// Semantic state colors (2025 UX standards)
const successGreen: MantineColorsTuple = [
  '#f0fdf4',
  '#dcfce7',
  '#bbf7d0',
  '#86efac',
  '#22c55e', // Primary success color
  '#16a34a',
  '#15803d',
  '#166534',
  '#14532d',
  '#052e16'
];

const errorRed: MantineColorsTuple = [
  '#fef2f2',
  '#fee2e2',
  '#fecaca',
  '#fca5a5',
  '#ef4444', // Primary error color
  '#dc2626',
  '#b91c1c',
  '#991b1b',
  '#7f1d1d',
  '#450a0a'
];

const infoBlue: MantineColorsTuple = [
  '#eff6ff',
  '#dbeafe',
  '#bfdbfe',
  '#93c5fd',
  '#3b82f6', // Primary info color
  '#2563eb',
  '#1d4ed8',
  '#1e40af',
  '#1e3a8a',
  '#172554'
];

const warningAmber: MantineColorsTuple = [
  '#fffbeb',
  '#fef3c7',
  '#fde68a',
  '#fcd34d',
  '#f59e0b', // Primary warning color
  '#d97706',
  '#b45309',
  '#92400e',
  '#78350f',
  '#451a03'
];

// Create the theme
export const theme = createTheme({
  colors: {
    mchdBlue,
    mchdGray,
    successGreen,
    errorRed,
    infoBlue,
    warningAmber,
  },
  primaryColor: 'mchdBlue',
  primaryShade: 4, // Use the main #28A8E0 color
  
  // Typography - Rubik for modern, professional look
  fontFamily: 'Rubik, Inter, system-ui, -apple-system, sans-serif',
  fontFamilyMonospace: 'JetBrains Mono, Monaco, Courier, monospace',
  
  // Headings
  headings: {
    fontFamily: 'Rubik, Inter, system-ui, -apple-system, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2.5rem', lineHeight: '1.2' },
      h2: { fontSize: '2rem', lineHeight: '1.3' },
      h3: { fontSize: '1.5rem', lineHeight: '1.4' },
      h4: { fontSize: '1.25rem', lineHeight: '1.4' },
      h5: { fontSize: '1.125rem', lineHeight: '1.4' },
      h6: { fontSize: '1rem', lineHeight: '1.4' },
    },
  },
  
  // Spacing
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  
  // Border radius - Updated to 12px system (2025 standard)
  radius: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '0.75rem',    // 12px (standard)
    lg: '1rem',       // 16px
    xl: '1.5rem',     // 24px
  },
  
  // Shadows
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  
  // Components
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
      styles: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(40, 168, 224, 0.3)',
          },
        },
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
      styles: {
        input: {
          transition: 'all 0.2s ease',
          '&:focus': {
            borderColor: '#28A8E0',
            boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)',
          },
        },
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
      styles: {
        input: {
          transition: 'all 0.2s ease',
          '&:focus': {
            borderColor: '#28A8E0',
            boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)',
          },
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 'lg',
        shadow: 'md',
      },
    },
  },
  
  // Other theme properties
  other: {
    // Custom properties for our app
    colors: {
      primary: '#28A8E0',
      background: '#F4F7FE',
      surface: '#FAFAFA',
      text: {
        primary: '#1a1a1a',
        secondary: '#6b7280',
        muted: '#9ca3af',
      },
      // Semantic state colors (2025 UX standards)
      states: {
        success: '#22c55e',      // Green 500
        error: '#ef4444',        // Red 500
        info: '#3b82f6',         // Blue 500
        warning: '#f59e0b',      // Amber 500
      },
      // Grid cell states
      grid: {
        available: '#E9FBE9',    // Light green
        occupied: '#E6F0FF',     // Light blue
        blocked: '#FFECEC',      // Light red
        conflict: '#FFF3E0',     // Light amber
        inactive: '#F3F4F6',     // Light gray
      },
    },
  },
});

export default theme;
